import { apiClient } from '../api-client/apiClientService';
import { Project } from '../../models/project.model';
import { mapToProject, mapToProjects } from '../../mappers/projectMapper';

class ProjectService {
  private endpoint = 'project';

  public async getProjects(): Promise<Project[]> {
    const response = await apiClient.get<any>(this.endpoint);
    return mapToProjects(response.data);
  }

  public async getProjectById(id: string): Promise<Project> {
    const response = await apiClient.get<any>(`${this.endpoint}/${id}`);
    return mapToProject(response.data);
  }

  public async getProjectsByOrganizationId(organizationId: string): Promise<Project[]> {
    const response = await apiClient.get<any>(`organization/${organizationId}/${this.endpoint}/`);

    // Map the projects and ensure they have the correct organizationId
    const projects = mapToProjects(response.data);

    return projects.map(project => ({
      ...project,
      organizationId: project.organizationId || organizationId
    }));
  }

  public async createProject(project: Partial<Project>): Promise<Project> {
    const response = await apiClient.post<any>(`organization/${project.organizationId}/${this.endpoint}/`, project);
    const mappedProject = mapToProject(response.data);

    // Make sure to preserve the organizationId from the input if it's not in the response
    if (!mappedProject.organizationId && project.organizationId) {
      mappedProject.organizationId = project.organizationId;
    }

    // Same for organizationName
    if (!mappedProject.organizationName && project.organizationName) {
      mappedProject.organizationName = project.organizationName;
    }

    return mappedProject;
  }

  public async updateProject(id: string, project: Partial<Project>): Promise<Project> {
    // Note: This method is for updating project details, not for setting a project as active
    // To set a project as active, use userService.setActiveProject instead
    const response = await apiClient.put<any>(`${this.endpoint}/${id}/`, {name: project.projectName});
    const mappedProject = mapToProject(response.data);

    // Ensure we use the input data to fill in any missing fields in the response
    return {
      ...mappedProject,
      projectId: mappedProject.projectId || id,
      projectName: mappedProject.projectName || project.projectName || '',
      organizationId: mappedProject.organizationId || project.organizationId || '',
      organizationName: mappedProject.organizationName || project.organizationName || '',
      insights: mappedProject.insights || project.insights || [],
      metaAccountId: mappedProject.metaAccountId || project.metaAccountId || '',
    };
  }

  public async deleteProject(id: string): Promise<void> {
    await apiClient.delete(`${this.endpoint}/${id}/`);
  }
}

export const projectService = new ProjectService();